/**
 * Wraps a CategoryBrowseRow with IntersectionObserver so it only
 * mounts when scrolled into view. Renders a lightweight placeholder
 * (just the category header) until visible.
 */
import { useRef, useState, useEffect, type ReactNode } from "react";
import { CategoryBrowseRow } from "./CategoryBrowseRow";
import type { ViewportTier } from "@/hooks/useViewportTier";

interface LazyCategoryRowProps {
  label: string;
  color: string;
  count: number;
  scenarios: { id: string }[];
  renderCard: (scenario: any) => ReactNode;
  viewportTier: ViewportTier;
}

export function LazyCategoryRow(props: LazyCategoryRowProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect(); // Only need to trigger once
        }
      },
      { rootMargin: "200px" }, // Start loading 200px before visible
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  if (!visible) {
    // Placeholder: just the header row + enough height for cards
    return (
      <div ref={ref} className="mb-10" style={{ minHeight: 320 }}>
        <div className="flex items-baseline gap-3 mb-4 px-12">
          <div
            className="w-1 h-6 rounded-full flex-shrink-0"
            style={{ backgroundColor: props.color }}
          />
          <h3
            style={{
              fontFamily: "'Instrument Serif', Georgia, serif",
              fontSize: "clamp(20px, 2.5vw, 28px)",
              fontWeight: 400,
              letterSpacing: "-0.3px",
              opacity: 0.4,
            }}
          >
            {props.label}
          </h3>
          <span style={{ fontSize: 13, opacity: 0.3 }}>
            {props.count} scenario{props.count !== 1 ? "s" : ""}
          </span>
        </div>
      </div>
    );
  }

  return <CategoryBrowseRow {...props} />;
}
